import React, { useEffect, useRef, useState } from 'react';
import { inject, observer } from "mobx-react";
import Modal from './../../components/modal';
import SupplierUnitsCodes from '../../components/supplier-units-codes';
import ClientForm from '../../components/client-form/client-form';
import {
    buildOptionsAfterReviewPreset,
    CLIENT_NON_CLIENT_REQUIRED_REVIEW_FIELDS,
    CLIENT_NON_CLIENT_REVIEW_FIELD_PRESET,
    CLIENT_ONLY_REQUIRED_REVIEW_FIELDS,
    CLIENT_ONLY_REVIEW_FIELD_PRESET,
    REVIEW_MODE_CLIENT_NON_CLIENT,
    REVIEW_MODE_CLIENT_ONLY,
} from '../../components/client-form/review-seed-presets';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faSpinner, faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { fetch } from '../../utils/api-wrapper';
import SupplierImportHelp from '../../components/supplier-import-help';
import Utils from '../../utils/utils';

const AdminClient = ({ data }) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [codeClientId, setCodeClientId] = useState(null);
    const [unitClientId, setUnitClientId] = useState(null);
    const [showImportSuppliers, setShowImportSuppliers] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [components, setComponents] = useState([]);
    const [invoiceStatuses, setInvoiceStatuses] = useState(["Finalized", "Completed", "Invalid", "Processing", "Validated", "Need_Attention", "Pending", "Approved", "Need_Approval", "Denied"]);
    const [pageSize, setPageSize] = useState(10);
    const [showClientModal, setShowClientModal] = useState(false);
    const [updatedClient, setUpdatedClient] = useState({});
    const updatedClientRef = useRef(updatedClient);
    // XPEN-3692: "load the XPENS defaults" confirmed on a client that is not
    // saved yet. It has no id, and the server would load the defaults into the
    // caller's own client, so the request waits until the client is created.
    const pendingDefaultImputationsRef = useRef(false);

    useEffect(() => {
        updatedClientRef.current = updatedClient;
    }, [updatedClient]);
    const [clientToDelete, setClientToDelete] = useState(null);
    const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
    const [showSupplierCodesModal, setShowSupplierCodesModal] = useState(false);


    useEffect(() => {
        refreshClients();
        fetchComponents();
    }, []);

    const refreshClients = () => {
        fetch("/clients/", "GET", {},
            response => {
                let clients = response.data.clients;
                setClients(clients);
                setLoading(false);
            },
            () => {
                setErrorMessage(data?.t?.read("supplier_error_loading_clients"))
                setLoading(false);
            }
        );
    };

    const fetchComponents = () => {
        fetch("/clients/components", "GET", {},
            response => {
                let components = response.data;
                setComponents(components);
            },
            () => {
            }
        );
    };

    const handleClientSubmit = (e) => {
        e.preventDefault();
        if (updatedClientRef.current._id) {
            updateClient();
        } else {
            createClient();
        }
    };

    const createClient = () => {
        const clientPayload = updatedClientRef.current;
        // Read when the request is sent: a slow create still loads the defaults
        // for the client it creates, even if a new form was opened meanwhile.
        const pendingDefaultImputations = pendingDefaultImputationsRef.current;
        const formData = new FormData();
        formData.append('client', JSON.stringify(clientPayload));
        fetch("/clients/", "POST", formData,
            (response) => {
                // POST /clients/ returns the new client's id under "user+id".
                const newClientId = response?.data?.["user+id"];
                if (pendingDefaultImputations && newClientId) {
                    requestDefaultImputations(newClientId);
                }
                setShowClientModal(false);
                setSuccessMessage(data.t.read("client_created_successfully"));
                refreshClients();
            },
            () => {
                // The choice is kept: the form stays open and the prompt is
                // already answered, so a successful retry must still load the
                // defaults.
                setErrorMessage(data.t.read("failed_to_create_client"));
            }
        );
    };

    const updateClient = () => {
        const updatedClientWithoutId = { ...updatedClientRef.current };
        const clientId = updatedClientWithoutId._id;
        delete updatedClientWithoutId._id;
        const formData = new FormData();
        formData.append('client', JSON.stringify(updatedClientWithoutId));

        fetch(`/clients/${clientId}`, "PUT", formData,
            () => {
                setShowClientModal(false);
                refreshClients();
                setSuccessMessage(data.t.read("client_updated_successfully"));
            },
            () => {
                setErrorMessage(data.t.read("failed_to_update_client"));
            }
        );
    };

    const openNewClientModal = () => {
        pendingDefaultImputationsRef.current = false;
        setUpdatedClient({
            name: "",
            emails: "",
            supplier_codes: [],
            supplier_units: [],
            users: [],
            config: {
                type: "legal",
                engine: "default",
                supplier_group: false,
                allow_self_approval_assignment: true,
                suppliers: {
                    allow_create: true,
                    allow_import: true
                },
                frontend_invoice: {
                    hide_na_fields: true,
                    hide_cost_details_table: false,
                    cost_details_table_title: "",
                    gl_details_table_title: "",
                    hide_work_date: false,
                    hide_office_in_gl_details_table: false,
                    hide_due_date: false,
                    hide_unit_in_gl_details_table: true,
                    approver_can_add_invoice: false,
                    hide_group_number: true
                },
                invoice_status: ["Finalized", "Completed", "Invalid", "Processing", "Validated", "Need_Attention", "Pending"],
                components: [],
                variance: null,
                roles: ["SuperAdmin", "Admin", "Manager", "Clerk", "Assistant"],
                invoice_exporter: {
                    group_invoice_by: "",
                    export_supplier: true,
                    detail_on_multiple_line: false,
                    column_amount: 0,
                    re_export_supplier: false,
                    csv_line_template_invoice: "",
                    csv_line_template_supplier: "",
                    taxes_gl: "",
                    separator: ";",
                    decimal_separator: ".",
                    details_column_amount: 0
                },
                fiscal_year_start: "",
                cost_type: "",
                cost_type_nin: "",
                logo: ""
            },
        });
        setShowClientModal(true);
    };

    const openUpdateClientModal = (client) => {
        setUpdatedClient({ ...client });
        setShowClientModal(true);
    };

    const cancelClientModal = () => {
        setUpdatedClient({});
        setShowClientModal(false);
    };

    const deleteClient = () => {
        if (clientToDelete) {
            fetch(`/clients/`, "DELETE", { "client_ids": [clientToDelete._id] },
                () => {
                    refreshClients();
                    setClientToDelete(null);
                    setShowDeleteClientModal(false);
                    setSuccessMessage(data.t.read("success_deleting_client"));
                },
                () => {
                    setErrorMessage(data.t.read("failed_to_delete_client"));
                }
            );
        }
    };

    const openDeleteClientModal = (client) => {
        setClientToDelete(client);
        setShowDeleteClientModal(true);
    };

    const cancelDeleteClientModal = () => {
        setClientToDelete(null);
        setShowDeleteClientModal(false);
    };

    const updateField = (fieldName, value, subField = null, subSubField = null) => {
        setUpdatedClient(prevState => {
            let updatedClientCopy = { ...prevState };

            if (subSubField != null) {
                if (!updatedClientCopy[fieldName][subField]) {
                    updatedClientCopy[fieldName][subField] = {};
                }
                updatedClientCopy[fieldName][subField][subSubField] = value;
            } else if (subField == null) {
                updatedClientCopy[fieldName] = value;
            } else {
                updatedClientCopy[fieldName][subField] = value;
            }
            return updatedClientCopy;
        });
    };

    const updateUnit = (idx, fieldName, value) => {
        setUpdatedClient(prevState => {
            const supplier_units = [...prevState.supplier_units];
            supplier_units[idx] = { ...supplier_units[idx], [fieldName]: value };
            return { ...prevState, supplier_units };
        });
    };

    const removeUnit = (idx) => {
        setUpdatedClient(prevState => {
            const supplier_units = [...prevState.supplier_units];
            supplier_units.splice(idx, 1);
            return { ...prevState, supplier_units };
        });
    };

    const addUnit = () => {
        setUpdatedClient(prevState => {
            const supplier_units = prevState.supplier_units ? [...prevState.supplier_units] : [];
            supplier_units.push({ unit: "", desc: "" });
            return { ...prevState, supplier_units };
        });
    };

    const toggleFrontendSection = (component) => {
        setUpdatedClient(prevState => {
            let components = prevState.config.components ? [...prevState.config.components] : [];
            let statuses = prevState.config.invoice_status ? [...prevState.config.invoice_status] : [];
            let roles = prevState.config.roles ? [...prevState.config.roles] : [];
            let idx = components.findIndex(comp => comp.id === component._id);
            if (idx === -1) {
                components.push({ name: component.name, id: component._id });
                if (component.name === "approval") {
                    ["Approved", "Need_Approval", "Denied"].forEach(status => {
                        if (!statuses.includes(status)) {
                            statuses.push(status);
                        }
                    });
                }
            } else {
                components.splice(idx, 1);
                if (component.name === "approval") {
                    statuses = statuses.filter(status => {
                        return !["Approved", "Need_Approval", "Denied"].includes(status)
                    })
                }
            }
            if (components.filter(c => ['approval', 'detail-approval'].includes(c.name))) {
                if (!roles.includes('Approver')) {
                    roles.push('Approver');
                }
            }
            else {
                roles = roles.filter(role => "Approver" !== role)
            }

            return { ...prevState, config: { ...prevState.config, components: components, invoice_status: statuses, roles: roles } };
        });
    };

    const toggleModuleOption = (componentId, optionKey) => {
        setUpdatedClient(prevState => {
            const components = prevState.config.components ? [...prevState.config.components] : [];
            const componentIndex = components.findIndex(comp => comp.id === componentId);
            
            if (componentIndex !== -1) {
                const component = { ...components[componentIndex] };
                const currentOptions = component.options ? [...component.options] : [];
                const optionIndex = currentOptions.findIndex(opt => opt.name === optionKey);
                
                if (optionIndex !== -1) {
                    currentOptions.splice(optionIndex, 1);
                } else {
                    currentOptions.push({ name: optionKey });
                }
                
                components[componentIndex] = { ...component, options: currentOptions };
            }
            
            return { ...prevState, config: { ...prevState.config, components: components } };
        });
    };

    const setExpenseFieldRequired = (fieldName, isRequired) => {
        setUpdatedClient(prevState => {
            const currentRequiredFields = prevState.config?.expenses_review?.required_fields || [];
            const requiredFields = isRequired
                ? [...new Set([...currentRequiredFields, fieldName])]
                : currentRequiredFields.filter(name => name !== fieldName);

            return {
                ...prevState,
                config: {
                    ...prevState.config,
                    expenses_review: {
                        ...prevState.config?.expenses_review,
                        required_fields: requiredFields,
                    },
                },
            };
        });
    };

    // XPEN-3652: order of the expense review form's main fields. A reset stores
    // null rather than deleting the key: the API keeps any key a save omits,
    // so a deleted key would silently keep the old order.
    const setExpenseFieldOrder = (fieldOrder) => {
        setUpdatedClient(prevState => ({
            ...prevState,
            config: {
                ...prevState.config,
                expenses_review: {
                    ...prevState.config?.expenses_review,
                    field_order: fieldOrder,
                },
            },
        }));
    };

    const setExpenseReviewMode = (mode, componentId, templateOptionNames = []) => {
        setUpdatedClient(prevState => {
            const config = prevState.config || {};
            let components = config.components ? [...config.components] : [];
            const expensesReview = config.expenses_review || {};
            let requiredFields = expensesReview.required_fields;

            const reviewModePreset = {
                [REVIEW_MODE_CLIENT_ONLY]: {
                    fields: CLIENT_ONLY_REVIEW_FIELD_PRESET,
                    requiredFields: CLIENT_ONLY_REQUIRED_REVIEW_FIELDS,
                },
                [REVIEW_MODE_CLIENT_NON_CLIENT]: {
                    fields: CLIENT_NON_CLIENT_REVIEW_FIELD_PRESET,
                    requiredFields: CLIENT_NON_CLIENT_REQUIRED_REVIEW_FIELDS,
                },
            }[mode];

            if (reviewModePreset) {
                let componentIndex = components.findIndex(comp => comp.id === componentId);
                if (componentIndex === -1) {
                    componentIndex = components.findIndex(comp => comp.name === 'expenses');
                }
                if (componentIndex !== -1) {
                    const component = { ...components[componentIndex] };
                    component.options = buildOptionsAfterReviewPreset(
                        component.options,
                        reviewModePreset.fields,
                        templateOptionNames
                    );
                    components[componentIndex] = component;
                }

                const allowedFields = new Set(templateOptionNames);
                requiredFields = reviewModePreset.requiredFields.filter(fieldName =>
                    allowedFields.has(fieldName)
                );
            }

            const nextExpensesReview = {
                ...expensesReview,
                mode,
            };
            if (requiredFields != null) {
                nextExpensesReview.required_fields = requiredFields;
            }

            return {
                ...prevState,
                config: {
                    ...config,
                    components,
                    expenses_review: nextExpensesReview,
                },
            };
        });
    };

    const markReviewSeedPromptAnswered = () => {
        setUpdatedClient(prevState => ({
            ...prevState,
            config: {
                ...prevState.config,
                expenses_review: {
                    ...prevState.config?.expenses_review,
                    default_imputations_prompt_answered: true,
                },
            },
        }));
    };

    const requestDefaultImputations = (clientId) => {
        fetch("/imputations/add-defaults", "POST", { id_client: clientId }, () => {}, () => {});
    };

    const addDefaultImputations = () => {
        if (updatedClient._id) {
            requestDefaultImputations(updatedClient._id);
            return;
        }
        pendingDefaultImputationsRef.current = true;
    };

    const toggleInvoiceStatus = (status) => {
        setUpdatedClient(prevState => {
            let statuses = prevState.config.invoice_status ? [...prevState.config.invoice_status] : [];
            let idx = statuses.findIndex(s => s === status);

            if (idx === -1) {
                statuses.push(status);
            } else {
                statuses.splice(idx, 1);
            }

            return { ...prevState, config: { ...prevState.config, invoice_status: statuses } };
        });
    };

    const setInvoiceStatusesArray = (statuses) => {
        setUpdatedClient(prevState => ({
            ...prevState,
            config: { ...prevState.config, invoice_status: statuses }
        }));
    };

    const prepareImportSuppliers = (f) => {
        if (f.length > 0) {
            setImportFile(f[0]);
        }
    };

    const processImportSuppliers = () => {
        if (importFile != null) {
            const formData = new FormData();
            formData.append("csv", importFile);
            formData.append("id_client", codeClientId);

            fetch("/suppliers/client-import", "POST", formData,
                response => {
                    setShowImportSuppliers(false);
                    setImportFile(null);
                    refreshClients();
                },
                () => {
                    // Do not branch on summary/errors/alerts like admin-suppliers.js does:
                    // the backend always sends a populated `summary` on every response,
                    // including a full rejection, so that branch would always be taken
                    // and this generic message would never show.
                    setShowImportSuppliers(false);
                    setImportFile(null);
                    setErrorMessage(data.t.read("suppliers_import_failed"));
                },
                true
            );
        }
    };

    const updateVariance = (variance) => {
        setUpdatedClient(prevState => ({
            ...prevState,
            config: { ...prevState.config, variance }
        }));
    };

    const updateCostType = (cost_type) => {
        setUpdatedClient(prevState => ({
            ...prevState,
            config: { ...prevState.config, cost_type }
        }));
    };

    const updateCostTypeNIN = (cost_type_nin) => {
        setUpdatedClient(prevState => ({
            ...prevState,
            config: { ...prevState.config, cost_type_nin }
        }));
    };

    const updateLogo = (files) => {
        const file = files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setUpdatedClient(prevState => ({
                    ...prevState,
                    config: { ...prevState.config, logo: reader.result }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const toggleExporterOption = (option) => {
        setUpdatedClient(prevState => ({
            ...prevState,
            config: {
                ...prevState.config,
                invoice_exporter: {
                    ...prevState.config.invoice_exporter,
                    [option]: !prevState.config.invoice_exporter[option]
                }
            }
        }));
    };

    const changeExporterValues = (field, e) => {
        const value = e.target ? e.target.value : e;
        setUpdatedClient(prevState => ({
            ...prevState,
            config: {
                ...prevState.config,
                invoice_exporter: {
                    ...prevState.config.invoice_exporter,
                    [field]: value
                }
            }
        }));
    };

    const changeColAmountExporter = (e) => {
        const value = parseInt(e.target.value, 10);
        setUpdatedClient(prevState => ({
            ...prevState,
            config: {
                ...prevState.config,
                invoice_exporter: {
                    ...prevState.config.invoice_exporter,
                    column_amount: value
                }
            }
        }));
    };

    const getInvoiceExporterOption = (option) => {
        return updatedClient.config?.invoice_exporter?.[option] || false;
    };

    const getColumnValueExporter = (n) => {
        const template = updatedClient.config?.invoice_exporter?.csv_line_template_invoice || "";
        const arr = template.split(";");
        const value = arr[n];
        if (value && value.startsWith("HardCoded:")) {
            return "HardCoded";
        }
        return value || "";
    };

    const getHardCodedValueExporter = (n) => {
        const template = updatedClient.config?.invoice_exporter?.csv_line_template_invoice || "";
        const arr = template.split(";");
        const value = arr[n];
        if (value && value.startsWith("HardCoded:")) {
            return value.substring("HardCoded:".length);
        }
        return "";
    };

    const changeColumnValueExporter = (value, n) => {
        setUpdatedClient(prevState => {
            const template = prevState.config.invoice_exporter.csv_line_template_invoice || "";
            const arr = template.split(";");
            arr[n] = value;
            const newTemplate = arr.join(";");
            return {
                ...prevState,
                config: {
                    ...prevState.config,
                    invoice_exporter: {
                        ...prevState.config.invoice_exporter,
                        csv_line_template_invoice: newTemplate
                    }
                }
            };
        });
    };

    const openSupplierCodeModal = (client) => {
        setCodeClientId(client._id)
        setShowSupplierCodesModal(true)
    }

    const closeSupplierCodesModal = () => {
        setCodeClientId(null)
        setShowSupplierCodesModal(false)
    }

    // Pagination logic
    const emptyRowsCount = Math.max(0, pageSize - clients.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    return !loading ? <div>
        <div>
            <div className="d-flex justify-content-end align-items-center px-4 py-2">
                <div className="d-flex">
                    <button onClick={openNewClientModal} type="button"
                        className='btn btn-primary btn-sm ml-2'>
                        {data.t.read("add")}
                    </button>
                </div>
            </div>
            <div className="mb-2 card rounded-lg admin-table">
                <table className='table font-small table-hover '>
                    <thead>
                        <tr>
                            <th className="col-1">{data.t.read("client_table.name")}</th>
                            <th className="col-1">{data.t.read("client_table.number_of_users")}</th>
                            <th className="col-2">{data.t.read("client_table.created_on")}</th>
                            <th className="col-2">{data.t.read("client_table.last_modified_on")}</th>
                            <th className="text-center col-2">{data.t.read("client_table.actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            clients.map(client => {
                                return <tr key={client._id}>
                                    <td className="col-1"
                                        onClick={() => openUpdateClientModal(client)}>{client.name}</td>
                                    <td className="col-1 text-center"
                                        onClick={() => openUpdateClientModal(client)}>{client.users_count}</td>
                                    <td className="col-2"
                                        onClick={() => openUpdateClientModal(client)}>{client.created_at ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: false,
                                        }).format(new Date(client.created_at?.$date)) : ""}</td>
                                    <td className="col-2"
                                        onClick={() => openUpdateClientModal(client)}>{client.updated_at ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: false,
                                        }).format(new Date(client.updated_at?.$date)) : ""}</td>
                                    <td className='col-2'>
                                        <div
                                            className="d-flex justify-content-center align-items-center">
                                            <button className='btn btn-info btn-sm ml-1'
                                                onClick={() => openSupplierCodeModal(client)}>{data.t.read("client_table.supplier_codes")}</button>
                                            <button className='btn btn-secondary btn-sm ml-1'
                                                onClick={() => setUnitClientId(client._id)}>{data.t.read("client_table.units_offices")}</button>
                                            <FontAwesomeIcon className="mx-2 my-auto"
                                                icon={faPencilAlt}
                                                onClick={() => openUpdateClientModal(client)}
                                                type="button" />
                                            <FontAwesomeIcon className="mx-2 my-auto"
                                                icon={faWindowClose} type="button"
                                                onClick={() => openDeleteClientModal(client)}
                                                style={{ color: 'red' }} />
                                        </div>
                                    </td>
                                </tr>
                            })
                        }
                        {
                            emptyRows.map((_, idx) => (
                                <tr key={`empty-${idx}`}>
                                    <td colSpan="7">&nbsp;</td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
        </div>
        {showDeleteClientModal && (
            <Modal
                title={(clients.find(c => c?._id === clientToDelete?._id))?.name}
                okAction={deleteClient}
                cancelAction={cancelDeleteClientModal}
                okText={data.t.read("delete")}
                cancelText={data.t.read("cancel")}>
                <p className='text-danger'>{data.t.read("confirm_delete_client")}</p>
            </Modal>
        )}
        <ClientForm
            showClientModal={showClientModal}
            updatedClient={updatedClient}
            components={components}
            invoiceStatuses={invoiceStatuses}
            data={data}
            onCancel={cancelClientModal}
            onSubmit={handleClientSubmit}
            updateField={updateField}
            updateUnit={updateUnit}
            removeUnit={removeUnit}
            addUnit={addUnit}
            toggleFrontendSection={toggleFrontendSection}
            toggleInvoiceStatus={toggleInvoiceStatus}
            setInvoiceStatuses={setInvoiceStatusesArray}
            updateVariance={updateVariance}
            updateCostType={updateCostType}
            updateCostTypeNIN={updateCostTypeNIN}
            updateLogo={updateLogo}
            toggleExporterOption={toggleExporterOption}
            changeExporterValues={changeExporterValues}
            changeColAmountExporter={changeColAmountExporter}
            getInvoiceExporterOption={getInvoiceExporterOption}
            getColumnValueExporter={getColumnValueExporter}
            getHardCodedValueExporter={getHardCodedValueExporter}
            changeColumnValueExporter={changeColumnValueExporter}
            toggleModuleOption={toggleModuleOption}
            addDefaultImputations={addDefaultImputations}
            reviewSeedPromptAnswered={
                updatedClient.config?.expenses_review?.default_imputations_prompt_answered === true
            }
            markReviewSeedPromptAnswered={markReviewSeedPromptAnswered}
            requiredExpenseFields={updatedClient.config?.expenses_review?.required_fields}
            setExpenseFieldRequired={setExpenseFieldRequired}
            expenseFieldOrder={updatedClient.config?.expenses_review?.field_order}
            setExpenseFieldOrder={setExpenseFieldOrder}
            reviewMode={updatedClient.config?.expenses_review?.mode}
            setExpenseReviewMode={setExpenseReviewMode}
        />
        {errorMessage != null && (
            <Modal title="Attention" okText={"OK"} okAction={() => setErrorMessage(null)}>
                <p style={{ whiteSpace: 'pre-line' }} className='text-danger'>{errorMessage}</p>
            </Modal>
        )}
        {successMessage != null && (
            <Modal title="Attention" okText={"OK"} okAction={() => setSuccessMessage(null)}>
                {successMessage}
            </Modal>
        )}
        {showImportSuppliers && (
            <Modal key="import-modal" width="50vw" title={data.t.read("import_suppliers")} okText={data.t.read("import")} okAction={() => processImportSuppliers()} cancelText={data.t.read("cancel")} cancelAction={() => { setShowImportSuppliers(false); setImportFile(null); setShowSupplierCodesModal(true) }} >
                <div>
                    <SupplierImportHelp data={data} variant="client" />
                    <div className='mt-2 mb-2'>
                        <input className='form-control-file' type="file" id="import-csv-file" data-testid="import-csv-file" onChange={f => prepareImportSuppliers(f.target.files)} />
                    </div>
                </div>
            </Modal>
        )}
        {/* XPEN-3267 — /suppliers/client-import imports into the caller's own
            tenant (the route reads auth_data.id_client), so the caller's own
            import switch is the one that governs this entry point. */}
        {showSupplierCodesModal && (
            <Modal title={data.t.read("code_supplier_association")} okText={"OK"}
                titleText={Utils.clientAllowsSupplierImport(data) ? data.t.read("import") : null}
                titleAction={Utils.clientAllowsSupplierImport(data) ? () => { setShowImportSuppliers(true); setShowSupplierCodesModal(false) } : null}
                okAction={() => closeSupplierCodesModal()}>
                <SupplierUnitsCodes type={"codes"} clientId={codeClientId} />
            </Modal>
        )}
        {unitClientId != null && (
            <Modal title={data.t.read("unit_office_association")} okText={"OK"} okAction={() => setUnitClientId(null)}>
                <SupplierUnitsCodes type={"units"} clientId={unitClientId} />
            </Modal>
        )}
    </div>
        :
        <div className="d-flex loading-container w-100">
            <FontAwesomeIcon className="infinite-rotate" icon={faSpinner} />
        </div>
    }

export default inject('data')(observer(AdminClient));
